/**
 * features/hr/DeductionsManager.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — the Payroll tab on a staff profile (Phase 1, Day 2).
 *
 * Three things live here:
 *   1. EarningsCard — basic salary, gratuity rate + eligibility, the
 *      per-staff allowance list, and bank details. Writes the new
 *      hrPayrollProfile (the real engine record; supersedes the old
 *      "Payroll reference" card, which still exists on the Contract
 *      tab and can be retired later).
 *   2. DeductionsSection — the "add deduction" dropdown over all 13
 *      types, the data-driven form for the chosen type (rendered from
 *      DEDUCTION_TYPES[type].fields, with a custom-fields section), and
 *      a restrained card per saved deduction.
 *   3. A live monthly summary (gross / deductions / indicative PAYE /
 *      indicative net) so the bursar sees the shape of the payslip
 *      before any pay run exists.
 *
 * The PAYE figure here is INDICATIVE only — labelled as such — using
 * the seeded FY2026 table. The authoritative number is stamped by the
 * pay run (Day 3 brings the live, versioned tax table).
 *
 * Card styling follows the house pattern (handover note 7.7): plain
 * SoftCard, status via a right-side badge, a subtle amber ring only on
 * items needing attention (a cleared loan awaiting archive). No heavy
 * coloured left bars.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Plus, Save, Loader2, Trash2, Pencil, Wallet, Banknote, Lock, AlertTriangle, CalendarClock, Archive, ChevronDown,
  HeartPulse, Landmark, Building2, HandCoins, Flower2, Car, Home, Zap, Droplets,
  ShieldAlert, GraduationCap, Receipt, CircleEllipsis, Stamp,
  type LucideIcon,
} from 'lucide-react';
import { SoftCard, StatChip, glassFieldClass } from '@/features/finance/ui';
import { AccentButton, FormModal } from '@/components/ui/glass';
import { SaveConfirmation, useAutoSave } from '@/components/ui/SaveConfirmation';
import { hr } from '@/features/hr/hrWrite';
import LoanActionsModal from '@/features/hr/LoanActionsModal';
import { useCompletedPayRuns } from '@/features/hr/usePayrollPeriods';
import { recordHrAudit } from '@/lib/hrAudit';
import { useAuth } from '@/contexts/AuthContext';
import { staffFullName, type HrStaff } from '@/lib/hr';
import {
  DEDUCTION_TYPES,
  DEDUCTION_TYPE_ORDER,
  FY2026_TAX_TABLE,
  deductionMonthlyAmount,
  deductionCurrentBalance,
  deductionForPeriod,
  deductionOpeningBalance,
  deductionStartPeriod,
  periodIndex,
  currentYearMonth,
  loanTotalWithInterest,
  impliedInstalments,
  monthlyGratuityAccrual,
  monthlyPAYE,
  round2,
  parseYearMonth,
  suggestedFirstMonthStr,
  firstOpenPayRunMonth,
  MONTH_LABELS,
  fmtPula,
  type HrPayrollProfile,
  type HrDeduction,
  type DeductionType,
  type DeductionStatus,
  type StaffAllowance,
  type FieldDef,
  type FieldValue,
  type AllowanceCatalogEntry,
  type HrPayrollSettings,
  type HrTaxTable,
} from '@/lib/hrPayroll';

/* A LucideIcon per deduction type (typed map; the config's icon string
   is advisory — the UI owns the actual components). */
const DEDUCTION_ICON: Record<DeductionType, LucideIcon> = {
  medical_aid: HeartPulse,
  salary_advance: HandCoins,
  school_loan: Landmark,
  bank_scheme: Building2,
  funeral_cover: Flower2,
  car_insurance: Car,
  rent: Home,
  electricity: Zap,
  water: Droplets,
  alarms: ShieldAlert,
  school_fees: GraduationCap,
  residence_permit: Stamp,
  paye: Receipt,
  other: CircleEllipsis,
};

const STATUS_BADGE: Record<DeductionStatus, { label: string; cls: string }> = {
  active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  paid_off: { label: 'Paid off — archive', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  archived: { label: 'Archived', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  suspended: { label: 'Suspended', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
};

/* The seeded FY2026 table, materialised for the indicative PAYE preview. */
const PREVIEW_TABLE: HrTaxTable = { ...FY2026_TAX_TABLE, createdAt: '', updatedAt: '' };

function numOf(v: FieldValue | undefined): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
}

/* ==================================================================
   PAYROLL TAB (default export)
   ================================================================== */
export default function PayrollTab({
  staff,
  profile,
  deductions,
  allowanceCatalog,
  settings,
  activeTaxTable,
  afterAllowances,
  showEarnings = true,
}: {
  staff: HrStaff;
  profile?: HrPayrollProfile;
  deductions: HrDeduction[];
  allowanceCatalog: AllowanceCatalogEntry[];
  settings?: HrPayrollSettings;
  activeTaxTable?: HrTaxTable;
  afterAllowances?: ReactNode;
  /** When false, hides the basic-pay / gratuity / allowances editor — it now
   *  lives on the dedicated Basic Pay tab, so the workspace stays lean. */
  showEarnings?: boolean;
}) {
  const mine = useMemo(
    () =>
      deductions
        .filter((d) => d.staffId === staff.id)
        .sort((a, b) => DEDUCTION_TYPE_ORDER.indexOf(a.type) - DEDUCTION_TYPE_ORDER.indexOf(b.type)),
    [deductions, staff.id],
  );

  const basic = profile?.basicSalary ?? 0;
  const allowanceTotal = round2((profile?.allowances ?? []).reduce((a, x) => a + (x.amount || 0), 0));
  const gross = round2(basic + allowanceTotal);
  const dedTotal = round2(
    mine.filter((d) => d.status === 'active').reduce((a, d) => a + deductionMonthlyAmount(d), 0),
  );
  const payeIndicative = monthlyPAYE(gross, activeTaxTable ?? PREVIEW_TABLE);
  const netIndicative = round2(gross - dedTotal - payeIndicative);

  return (
    <div className="space-y-4">
      {/* Live monthly summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatChip tone="blue" value={fmtPula(gross)} label="Gross (monthly)" icon={Banknote} />
        <StatChip tone="red" value={fmtPula(round2(dedTotal + payeIndicative))} label="Deductions + PAYE" />
        <StatChip tone="gold" value={fmtPula(payeIndicative)} label="PAYE (indicative)" />
        <StatChip tone="emerald" value={fmtPula(netIndicative)} label="Net (indicative)" />
      </div>

      {showEarnings && (
        <>
          <EarningsCard staff={staff} profile={profile} allowanceCatalog={allowanceCatalog} />
          {afterAllowances}
        </>
      )}

      <DeductionsSection staff={staff} deductions={mine} settings={settings} />
    </div>
  );
}

/* ==================================================================
   EARNINGS CARD — basic salary, gratuity, allowances, bank
   ================================================================== */
function EarningsCard({
  staff,
  profile,
  allowanceCatalog,
}: {
  staff: HrStaff;
  profile?: HrPayrollProfile;
  allowanceCatalog: AllowanceCatalogEntry[];
}) {
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? null };
  const [basicSalary, setBasicSalary] = useState(profile ? String(profile.basicSalary) : '');
  const [salaryBand, setSalaryBand] = useState(profile?.salaryBand ?? '');
  const [gratuityRatePct, setGratuityRatePct] = useState(profile ? String(profile.gratuityRatePct) : '');
  const [gratuityEligible, setGratuityEligible] = useState(profile?.gratuityEligible ?? true);
  const [openingBasicPaid, setOpeningBasicPaid] = useState(
    profile?.openingBasicPaidThisContract != null ? String(profile.openingBasicPaidThisContract) : '',
  );
  const [allowances, setAllowances] = useState<StaffAllowance[]>(profile?.allowances ?? []);
  const [dirty, setDirty] = useState(false);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  const catalogLabels = allowanceCatalog.filter((a) => a.active).map((a) => a.label);
  const accrual = monthlyGratuityAccrual(numOf(basicSalary), numOf(gratuityRatePct));

  // Mark dirty + schedule autosave on any change (skip first render).
  const firstRender = useRef(true);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  function setAllowance(i: number, patch: Partial<StaffAllowance>) {
    setAllowances((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  }
  function addAllowance() {
    setAllowances((prev) => [...prev, { type: '', amount: 0 }]);
  }
  function removeAllowance(i: number) {
    setAllowances((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function save() {
    setSaveModal({ show: true, status: 'saving', message: 'Saving payroll details…' });
    try {
      const cleanAllowances = allowances
        .map((a) => ({ type: a.type.trim(), amount: round2(Number(a.amount) || 0) }))
        .filter((a) => a.type && a.amount !== 0);
      await hr.payrollProfile.set(staff.id, {
        staffId: staff.id,
        basicSalary: round2(numOf(basicSalary)),
        salaryBand: salaryBand.trim() || undefined,
        allowances: cleanAllowances,
        gratuityRatePct: round2(numOf(gratuityRatePct)),
        gratuityEligible,
        openingBasicPaidThisContract: openingBasicPaid.trim() === '' ? undefined : round2(numOf(openingBasicPaid)),
        // Bank/payment account is edited on the Staff Profile (Contract tab),
        // not in the month-to-month workspace — preserve it untouched here.
        ...(profile?.bank ? { bank: profile.bank } : {}),
      });
      setDirty(false);
      await recordHrAudit({
        action: 'profile.update',
        actor,
        subjectId: staff.id,
        after: {
          basicSalary: round2(numOf(basicSalary)),
          gratuityRatePct: round2(numOf(gratuityRatePct)),
          gratuityEligible,
          allowances: cleanAllowances.length,
        },
      });
      setSaveModal({ show: true, status: 'success', message: 'Payroll details saved.' });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    }
  }

  // Always keep a ref to the latest save() so the unmount flush below
  // uses current state, not a stale closure.
  const saveRef = useRef(save);
  saveRef.current = save;

  // Auto-save after 2.5s of inactivity.
  const { touch } = useAutoSave(
    async () => { if (dirtyRef.current) await save(); },
    300000,
    true,
  );

  // Flush any pending change immediately when leaving this editor (e.g.
  // switching staff, closing the workspace, opening another form) so a
  // typed-but-not-yet-autosaved value is never lost.
  useEffect(() => () => { if (dirtyRef.current) void saveRef.current(); }, []);

  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    setDirty(true);
    touch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicSalary, salaryBand, gratuityRatePct, gratuityEligible, openingBasicPaid, allowances]);

  return (
    <SoftCard hover={false}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
          <Wallet size={16} /> Earnings & gratuity
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {dirty ? 'Unsaved — saving shortly…' : 'All changes saved'}
          </span>
          <AccentButton icon={saveModal.show && saveModal.status === 'saving' ? Loader2 : Save} disabled={!dirty} onClick={save}>
            Save
          </AccentButton>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3">
        <Field label="Basic salary (monthly, BWP)">
          <input
            type="number"
            inputMode="decimal"
            value={basicSalary}
            onChange={(e) => setBasicSalary(e.target.value)}
            placeholder="e.g. 12000"
            className={glassFieldClass('py-2')}
          />
        </Field>
        <Field label="Salary band (label only — drives no math)">
          <input value={salaryBand} onChange={(e) => setSalaryBand(e.target.value)} placeholder="e.g. C3" className={glassFieldClass('py-2')} />
        </Field>
        <Field label="Gratuity rate (%)">
          <input
            type="number"
            inputMode="decimal"
            value={gratuityRatePct}
            onChange={(e) => setGratuityRatePct(e.target.value)}
            placeholder="e.g. 25"
            className={glassFieldClass('py-2')}
          />
        </Field>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
            <input
              type="checkbox"
              checked={gratuityEligible}
              onChange={(e) => setGratuityEligible(e.target.checked)}
              className="rounded"
            />
            Accrues gratuity
          </label>
          {gratuityEligible && numOf(gratuityRatePct) > 0 && (
            <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
              ≈ {fmtPula(accrual)} / month
            </span>
          )}
        </div>
        {gratuityEligible && (
          <Field label="Opening basic paid this contract (at go-live)" hint="Total basic salary already paid in the current contract before the system. Gratuity = rate × (this + basic paid since). Leave blank/0 for staff starting fresh.">
            <input
              type="number"
              inputMode="decimal"
              value={openingBasicPaid}
              onChange={(e) => setOpeningBasicPaid(e.target.value)}
              placeholder="e.g. 240000"
              className={glassFieldClass('py-2')}
            />
          </Field>
        )}
      </div>

      {/* Allowances */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Allowances</h4>
          <button onClick={addAllowance} className="flex items-center gap-1 text-xs font-medium text-primary dark:text-primarySoft hover:underline">
            <Plus size={13} /> Add allowance
          </button>
        </div>
        {allowances.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 py-1">No allowances. Basic salary only.</p>
        ) : (
          <div className="space-y-2">
            <datalist id={`allowance-types-${staff.id}`}>
              {catalogLabels.map((l) => <option key={l} value={l} />)}
            </datalist>
            {allowances.map((a, i) => (
              <div key={i} className="grid grid-cols-[1fr_8rem_auto] gap-2 items-center">
                <input
                  list={`allowance-types-${staff.id}`}
                  value={a.type}
                  onChange={(e) => {
                    const v = e.target.value;
                    const cat = allowanceCatalog.find((c) => c.active && c.label.toLowerCase() === v.toLowerCase());
                    if (cat && !a.amount && cat.defaultAmount != null) setAllowance(i, { type: v, amount: cat.defaultAmount });
                    else setAllowance(i, { type: v });
                  }}
                  placeholder="Allowance (pick or type, e.g. Housing)"
                  className={glassFieldClass('py-1.5 text-sm')}
                />
                <input
                  type="number"
                  inputMode="decimal"
                  value={a.amount === 0 ? '' : a.amount}
                  onChange={(e) => setAllowance(i, { amount: Number(e.target.value) || 0 })}
                  placeholder="Amount"
                  className={glassFieldClass('py-1.5 text-sm')}
                />
                <button onClick={() => removeAllowance(i)} className="text-gray-400 hover:text-red-500 p-1" title="Remove">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal((m) => ({ ...m, show: false }))}
      />
    </SoftCard>
  );
}

/* ==================================================================
   DEDUCTIONS SECTION — picker + cards
   ================================================================== */
function DeductionsSection({
  staff,
  deductions,
  settings,
}: {
  staff: HrStaff;
  deductions: HrDeduction[];
  settings?: HrPayrollSettings;
}) {
  const [adding, setAdding] = useState<DeductionType | ''>('');
  const [editing, setEditing] = useState<HrDeduction | null>(null);
  /* ARCHIVED DEDUCTIONS ARE NOT DELETED, BUT THEY ARE NOT THE LIST EITHER.
     Consolidating a staff member's school loans archives the ones folded in,
     which left two dead cards sitting among the live ones showing balances
     that are no longer collected. A reader has to check a badge on every card
     to know which figures still mean anything.

     So the grid shows what is actually running, and the archived ones move
     behind a disclosure below it — present, countable and one click away,
     because they are the audit trail of what was borrowed and when. */
  const [showArchived, setShowArchived] = useState(false);
  /* Loans get their own actions — change the instalment, top up, stop —
     because each appends to a history rather than overwriting a field. */
  const [managing, setManaging] = useState<HrDeduction | null>(null);

  const live = deductions.filter((d) => d.status !== 'archived');
  const archived = deductions.filter((d) => d.status === 'archived');

  return (
    <SoftCard hover={false}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
          <Receipt size={16} /> Deductions
          {live.length > 0 && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {live.length}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={adding}
            onChange={(e) => setAdding(e.target.value as DeductionType | '')}
            className={glassFieldClass('py-2 text-sm w-48')}
          >
            <option value="">Add deduction…</option>
            {DEDUCTION_TYPE_ORDER.filter((t) => t !== 'paye').map((t) => (
              <option key={t} value={t}>{DEDUCTION_TYPES[t].label}</option>
            ))}
          </select>
        </div>
      </div>

      {live.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
          {archived.length > 0
            ? 'Nothing is being deducted. There are archived deductions below.'
            : 'No deductions on file. Use “Add deduction” to attach one. PAYE is computed automatically at the pay run.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {live.map((d, i) => (
            <DeductionCard key={d.id} index={i + 1} deduction={d} onEdit={() => setEditing(d)} onManage={() => setManaging(d)} />
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            aria-expanded={showArchived}
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white"
          >
            <Archive size={13} />
            Archived ({archived.length})
            <ChevronDown size={13} className={showArchived ? 'rotate-180 transition-transform' : 'transition-transform'} />
          </button>
          {showArchived && (
            <>
              <p className="text-[11px] text-gray-400 mt-2 mb-3">
                Not deducted and not counted in any total. Kept as the record of what was borrowed —
                a consolidated loan archives the ones folded into it.
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 opacity-70">
                {archived.map((d, i) => (
                  <DeductionCard key={d.id} index={i + 1} deduction={d} onEdit={() => setEditing(d)} onManage={() => setManaging(d)} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {adding && (
        <DeductionFormModal
          staff={staff}
          type={adding}
          settings={settings}
          onClose={() => setAdding('')}
        />
      )}
      {editing && (
        <DeductionFormModal
          staff={staff}
          type={editing.type}
          existing={editing}
          settings={settings}
          onClose={() => setEditing(null)}
        />
      )}
      {managing && (
        <LoanActionsModal
          deduction={managing}
          staffName={`${staff.firstName} ${staff.lastName}`.trim()}
          onClose={() => setManaging(null)}
        />
      )}
    </SoftCard>
  );
}

/** Short month names for the "this month" line on a deduction card. */
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function DeductionCard({ deduction: d, index, onEdit, onManage }: { deduction: HrDeduction; index: number; onEdit: () => void; onManage: () => void }) {
  const Icon = DEDUCTION_ICON[d.type];
  const agreed = deductionMonthlyAmount(d);
  // Live outstanding balance — same engine as the payslip. The stored
  // remainingBalance is only a seed and is never decremented as payroll runs.
  const balance = deductionCurrentBalance(d);
  const cleared = balance !== null && balance <= 0 && d.status === 'active';
  const badge = STATUS_BADGE[cleared ? 'paid_off' : d.status];
  const needsAttention = cleared || d.status === 'paid_off';

  /* WHAT COMES OFF THIS MONTH, which is not always the agreed figure.
     An instalment can be changed or paused for a month, and the final
     payment is capped to whatever is left — so the amount actually taken can
     differ from the standing arrangement without anything being wrong. It can
     also differ because somebody changed it and did not say so. Both cases
     look identical on a card that only shows the agreed amount, which is why
     the school asked for this. Same engine as the payslip, so what is shown
     here is what will be deducted. */
  const period = currentYearMonth();
  const thisMonth = deductionForPeriod(d, period).amount;
  const deviates = round2(thisMonth) !== round2(agreed);
  const monthLabel = MONTH_ABBR[period.month - 1];

  return (
    <div
      className={`relative rounded-xl border bg-white dark:bg-gray-800 p-4 shadow-sm ${
        needsAttention
          ? 'border-amber-300 dark:border-amber-700 ring-1 ring-amber-200 dark:ring-amber-800/60'
          : 'border-gray-200 dark:border-gray-700'
      }`}
    >
      <span className="absolute -top-2 -left-2 h-5 min-w-5 px-1 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center shadow">
        {index}
      </span>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 h-9 w-9 shrink-0 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300">
          <Icon size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="font-medium text-gray-800 dark:text-gray-100 truncate">{d.label}</p>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${badge.cls}`}>
              {badge.label}
            </span>
          </div>
          {cardSubtitle(d) && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{cardSubtitle(d)}</p>}

          <div className="mt-2 flex items-end justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {fmtPula(agreed)} <span className="text-xs font-normal text-gray-400">/ month agreed</span>
              </p>

              {/* This month, always shown — highlighted only when it differs. */}
              <p
                className={`text-xs mt-0.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${
                  deviates
                    ? 'bg-amber-100 text-amber-800 font-semibold dark:bg-amber-900/40 dark:text-amber-200'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
                title={
                  deviates
                    ? `${monthLabel} differs from the agreed ${fmtPula(agreed)} a month`
                    : `${monthLabel} matches the agreed amount`
                }
              >
                {deviates && <AlertTriangle size={11} />}
                {monthLabel}: {fmtPula(thisMonth)}
              </p>

              {balance !== null && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Balance: {fmtPula(balance)}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              {DEDUCTION_TYPES[d.type].group === 'loan' && d.status === 'active' && (
                <button onClick={onManage} className="flex items-center gap-1 text-xs font-semibold text-primary dark:text-primarySoft hover:underline">
                  <CalendarClock size={12} /> Manage
                </button>
              )}
              <button onClick={onEdit} className="flex items-center gap-1 text-xs font-medium text-primary dark:text-primarySoft hover:underline">
                <Pencil size={12} /> Edit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function cardSubtitle(d: HrDeduction): string {
  const v = d.values;
  switch (d.type) {
    case 'medical_aid': return [v.plan, v.membershipNumber].filter(Boolean).join(' · ') as string;
    case 'bank_scheme': return [v.bankName, v.reference].filter(Boolean).join(' · ') as string;
    case 'funeral_cover':
    case 'car_insurance':
    case 'alarms': return [v.provider, v.policyNumber, v.reference].filter(Boolean).join(' · ') as string;
    case 'salary_advance': return v.reason ? String(v.reason) : 'Salary advance';
    case 'school_loan': return v.reason ? String(v.reason) : 'Loan / advance';
    case 'school_fees': return v.childrenCovered ? String(v.childrenCovered) : 'Staff children fees';
    case 'residence_permit': return [v.permitType, v.permitNumber].filter(Boolean).join(' · ') as string;
    case 'other': return v.name ? String(v.name) : '';
    default: return '';
  }
}

/** "September 2026" — used where the office has to recognise a month in a
 *  sentence rather than read it off a grid. */
function ymLabel(p: { year: number; month: number }): string {
  return `${MONTH_LABELS[p.month - 1]} ${p.year}`;
}

/* ==================================================================
   DATA-DRIVEN DEDUCTION FORM
   ================================================================== */
export type FormState = Record<string, string | boolean>;

export function seedForm(
  type: DeductionType,
  existing?: HrDeduction,
  completedRuns: Iterable<string> = [],
): FormState {
  const cfg = DEDUCTION_TYPES[type];
  const out: FormState = {};
  const now = new Date();
  const nowYmd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const firstMonthDefault = suggestedFirstMonthStr(completedRuns, now);
  for (const f of cfg.fields) {
    const cur = existing?.values?.[f.key];
    if (cur !== undefined && cur !== null) {
      out[f.key] = typeof cur === 'boolean' ? cur : String(cur);
    } else if (f.default !== undefined && f.default !== null) {
      out[f.key] = typeof f.default === 'boolean' ? f.default : String(f.default);
    } else if (!existing && (f.key === 'firstMonth' || f.key === 'firstRepaymentMonth')) {
      // New record: anchor to the first month whose pay run has not been done.
      out[f.key] = firstMonthDefault;
    } else if (!existing && (f.key === 'loanDate' || f.key === 'advanceDate')) {
      // Informational "date granted" fields default to today (records, not anchors).
      out[f.key] = nowYmd;
    } else {
      out[f.key] = f.kind === 'checkbox' ? false : '';
    }
  }
  return out;
}

function n(form: FormState, key: string): number {
  const raw = form[key];
  const v = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''));
  return Number.isFinite(v) ? v : 0;
}

/** "Month YYYY" of the final instalment, from the first month + count. */
function lastRepaymentLabel(firstISO: string, instalments: number): string {
  if (!firstISO || instalments <= 0) return '';
  const ym = parseYearMonth(firstISO);
  if (!ym) return '';
  // Advance (instalments - 1) whole months from the first month, in plain
  // integer arithmetic — no Date string parsing (avoids timezone shift).
  const zeroBased = (ym.month - 1) + (instalments - 1);
  const year = ym.year + Math.floor(zeroBased / 12);
  const month = ((zeroBased % 12) + 12) % 12; // 0-based month
  const end = new Date(year, month, 1);
  return end.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

/** Recompute derived fields after an edit. Returns the keys to patch. */
export function recompute(type: DeductionType, form: FormState, employerPct: number, changedKey?: string): FormState {
  const patch: FormState = {};
  switch (type) {
    case 'medical_aid': {
      const premium = n(form, 'monthlyPremium');
      const employee = round2(premium * (1 - employerPct / 100));
      patch.employeeContribution = String(employee);
      patch.employerContribution = String(round2(premium - employee));
      break;
    }
    case 'salary_advance': {
      // Recovered whole in one payroll: nothing to spread, so only the balance follows the amount.
      patch.remainingBalance = String(n(form, 'loanAmount'));
      break;
    }
    case 'school_loan': {
      const total = loanTotalWithInterest(n(form, 'loanAmount'), n(form, 'interestRatePct'));
      patch.remainingBalance = String(total);
      const monthly = n(form, 'monthlyRepayment');
      let insts = n(form, 'instalments');
      if (changedKey === 'instalments') {
        // Instalments drives the monthly repayment.
        if (insts > 0) patch.monthlyRepayment = String(round2(total / insts));
      } else if (changedKey !== 'firstRepaymentMonth' && monthly > 0) {
        // Monthly repayment (or the loan total) drives the instalments.
        insts = impliedInstalments(total, monthly);
        patch.instalments = String(insts);
      }
      patch.lastRepaymentMonth = lastRepaymentLabel(String(form.firstRepaymentMonth ?? ''), insts);
      break;
    }
    case 'bank_scheme': {
      const orig = n(form, 'originalAmount');
      let monthly = n(form, 'monthlyDeduction');
      let insts = n(form, 'totalInstalments');
      if (changedKey === 'totalInstalments') {
        if (insts > 0) { monthly = round2(orig / insts); patch.monthlyDeduction = String(monthly); }
      } else if (changedKey !== 'instalmentsCompleted' && monthly > 0) {
        insts = impliedInstalments(orig, monthly);
        patch.totalInstalments = String(insts);
      }
      const done = n(form, 'instalmentsCompleted');
      patch.remainingBalance = String(round2(Math.max(0, orig - monthly * done)));
      break;
    }
    case 'school_fees': {
      const total = n(form, 'totalFees');
      let monthly = n(form, 'monthlyAmount');
      let insts = n(form, 'instalments');
      if (changedKey === 'instalments') {
        if (insts > 0) { monthly = round2(total / insts); patch.monthlyAmount = String(monthly); }
      } else if (changedKey !== 'instalmentsCompleted' && monthly > 0) {
        insts = impliedInstalments(total, monthly);
        patch.instalments = String(insts);
      }
      const done = n(form, 'instalmentsCompleted');
      patch.remainingBalance = String(round2(Math.max(0, total - monthly * done)));
      break;
    }
    case 'residence_permit': {
      const total = n(form, 'totalCost');
      let monthly = n(form, 'monthlyAmount');
      let insts = n(form, 'instalments');
      if (changedKey === 'instalments') {
        if (insts > 0) { monthly = round2(total / insts); patch.monthlyAmount = String(monthly); }
      } else if (changedKey !== 'instalmentsCompleted' && monthly > 0) {
        insts = impliedInstalments(total, monthly);
        patch.instalments = String(insts);
      }
      const done = n(form, 'instalmentsCompleted');
      patch.remainingBalance = String(round2(Math.max(0, total - monthly * done)));
      break;
    }
    default:
      break;
  }
  return patch;
}

/** Which keys trigger a recompute for each type. */
export const RECOMPUTE_TRIGGERS: Partial<Record<DeductionType, string[]>> = {
  medical_aid: ['monthlyPremium'],
  salary_advance: ['loanAmount'],
  school_loan: ['loanAmount', 'interestRatePct', 'monthlyRepayment', 'instalments', 'firstRepaymentMonth'],
  bank_scheme: ['originalAmount', 'monthlyDeduction', 'totalInstalments', 'instalmentsCompleted'],
  school_fees: ['totalFees', 'monthlyAmount', 'instalments', 'instalmentsCompleted'],
  residence_permit: ['totalCost', 'monthlyAmount', 'instalments', 'instalmentsCompleted'],
};

export function DeductionFormModal({
  staff,
  type,
  existing,
  settings,
  preset,
  onClose,
}: {
  staff: HrStaff;
  type: DeductionType;
  existing?: HrDeduction;
  settings?: HrPayrollSettings;
  preset?: Record<string, string>;
  onClose: () => void;
}) {
  const cfg = DEDUCTION_TYPES[type];
  const employerPct = settings?.medicalAidEmployerPct ?? 50;
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? null };
  const { completedRuns } = useCompletedPayRuns();
  const [confirmWarnings, setConfirmWarnings] = useState(false);

  // Safeguards: a balance-tracked deduction (loan / advance / scheme / fees /
  // permit) is "in progress" once its first repayment month has arrived. While
  // in progress, its financial terms are locked (editing them would silently
  // rewrite the derived schedule), and it cannot be hard-deleted.
  const isBalanceTracked = !!cfg.balanceKey;
  const lockTerms =
    !!existing &&
    isBalanceTracked &&
    deductionOpeningBalance(existing) > 0 &&
    periodIndex(deductionStartPeriod(existing)) <=
      periodIndex(currentYearMonth());

  const [form, setForm] = useState<FormState>(() => {
    const base = seedForm(type, existing, completedRuns);
    if (!existing) {
      const seeded = preset ? { ...base, ...preset } : base;
      // apply initial computed values where derivable
      return { ...seeded, ...recompute(type, seeded, employerPct) };
    }
    return base;
  });
  const [customFields, setCustomFields] = useState<{ key: string; value: string }[]>(
    existing?.customFields ?? [],
  );
  const [status, setStatus] = useState<DeductionStatus>(existing?.status ?? 'active');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  function update(key: string, value: string | boolean) {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      const triggers = RECOMPUTE_TRIGGERS[type] ?? [];
      if (triggers.includes(key)) {
        return { ...next, ...recompute(type, next, employerPct, key) };
      }
      return next;
    });
  }

  function buildValues(): Record<string, FieldValue> {
    const values: Record<string, FieldValue> = {};
    for (const f of cfg.fields) {
      const raw = form[f.key];
      if (f.kind === 'checkbox') {
        values[f.key] = Boolean(raw);
      } else if (f.kind === 'number' || f.kind === 'money') {
        values[f.key] = raw === '' || raw === undefined ? null : round2(Number(raw) || 0);
      } else {
        const s = String(raw ?? '').trim();
        values[f.key] = s === '' ? null : s;
      }
    }
    return values;
  }

  /* WHAT THE OFFICE IS ABOUT TO AGREE TO.
     Two things used to happen quietly and be discovered on a payslip: a
     deduction saved with no instalment, which collects nothing forever, and
     one dated past the next pay run, which collects nothing yet. Both are
     legitimate — a loan can be agreed before its repayment is settled, and a
     start can be deliberately deferred — so neither is blocked. They are put
     in front of whoever is saving, in the month names they will be asked
     about later. */
  const pendingWarnings = useMemo(() => {
    const out: string[] = [];
    const values = buildValues();
    const instalment = deductionMonthlyAmount({ type, values } as HrDeduction);
    if (isBalanceTracked && instalment <= 0) {
      out.push('No monthly instalment is set, so this will collect nothing until one is entered.');
    }
    const stated = parseYearMonth(values.firstMonth ?? values.firstRepaymentMonth ?? values.startDate);
    const open = firstOpenPayRunMonth(completedRuns);
    if (stated && periodIndex(stated) > periodIndex(open)) {
      out.push(
        `First repayment is set to ${ymLabel(stated)}, so this will NOT appear in the `
        + `${ymLabel(open)} pay run — the next one to be produced.`,
      );
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, type, completedRuns, isBalanceTracked]);

  async function save(confirmed = false) {
    if (!confirmed && pendingWarnings.length) { setConfirmWarnings(true); return; }
    setConfirmWarnings(false);
    setSaving(true);
    setSaveModal({ show: true, status: 'saving', message: 'Saving deduction…' });
    try {
      const values = buildValues();
      // Guard: a balance-tracked type with no opening amount would silently
      // become a perpetual deduction (never pays off). Require an amount.
      if (isBalanceTracked && deductionOpeningBalance({ type, values } as HrDeduction) <= 0) {
        setSaving(false);
        setSaveModal({
          show: true,
          status: 'error',
          message: 'Enter the total amount (loan/scheme/permit) so the balance can be tracked to payoff.',
        });
        return;
      }
      /* A new advance comes off the first payroll not yet done — decided here,
         at save, from the pay runs on record, so a form opened before they had
         loaded (or before this month's run was done) cannot pick the wrong month. */
      if (type === 'salary_advance' && !existing) {
        values.firstRepaymentMonth = suggestedFirstMonthStr(completedRuns);
      }
      const payload = {
        staffId: staff.id,
        type,
        label: cfg.label,
        status,
        values,
        customFields: customFields.filter((c) => c.key.trim()),
      };
      if (existing) await hr.deductions.update(existing.id, payload);
      else await hr.deductions.add(payload);
      await recordHrAudit({
        action: existing ? 'deduction.update' : 'deduction.create',
        actor,
        subjectId: existing ? existing.id : staff.id,
        after: { staffId: staff.id, type, label: cfg.label, status },
      });
      setSaveModal({ show: true, status: 'success', message: `${cfg.label} saved.` });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!existing) return;
    setDeleting(true);
    setSaveModal({ show: true, status: 'saving', message: 'Removing deduction…' });
    try {
      await hr.deductions.delete(existing.id);
      await recordHrAudit({
        action: 'deduction.delete',
        actor,
        subjectId: existing.id,
        before: { staffId: staff.id, type, label: cfg.label },
      });
      setSaveModal({ show: true, status: 'success', message: `${cfg.label} removed.` });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setDeleting(false);
    }
  }

  /** Suspend or archive in place — the safe alternative to deleting a loan
   *  that has started repaying (preserves the record + audit trail). */
  async function quickStatus(s: DeductionStatus) {
    if (!existing) return;
    setSaving(true);
    setSaveModal({ show: true, status: 'saving', message: `${s === 'suspended' ? 'Suspending' : 'Archiving'}…` });
    try {
      await hr.deductions.update(existing.id, { status: s });
      await recordHrAudit({
        action: 'deduction.update',
        actor,
        subjectId: existing.id,
        before: { status: existing.status },
        after: { staffId: staff.id, type, label: cfg.label, status: s },
      });
      setSaveModal({ show: true, status: 'success', message: `${cfg.label} ${s}.` });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  const Icon = DEDUCTION_ICON[type];

  return (
    <Modal
      title={`${existing ? 'Edit' : 'Add'} ${cfg.label}`}
      subtitle={`${staffFullName(staff)} · ${cfg.blurb}`}
      icon={Icon}
      onClose={onClose}
    >
      <div className="space-y-3">
        {lockTerms && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
            <Lock size={14} className="mt-0.5 shrink-0" />
            <span>
              Repayment has begun, so the amount and term are locked — editing them would rewrite the
              repayment schedule. To change the terms, suspend or archive this and create a new deduction
              for the remaining balance. You can still change the status below.
            </span>
          </div>
        )}
        {cfg.fields.map((f) => (
          <FieldInput
            key={f.key}
            def={f}
            value={form[f.key]}
            options={mergedOptions(f, settings)}
            onChange={(v) => update(f.key, v)}
            locked={lockTerms}
          />
        ))}

        {/* Status (not shown for PAYE which is engine-driven) */}
        {type !== 'paye' && (
          <Field label="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value as DeductionStatus)} className={glassFieldClass('py-2')}>
              <option value="active">Active</option>
              <option value="paid_off">Paid off — awaiting archive</option>
              <option value="suspended">Suspended</option>
              <option value="archived">Archived</option>
            </select>
          </Field>
        )}

        {/* Custom fields */}
        <CustomFieldsEditor fields={customFields} onChange={setCustomFields} />
      </div>

      {confirmDel && existing && (
        <div className={`mt-3 rounded-xl border px-3 py-3 text-sm ${lockTerms ? 'border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-900/20' : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'}`}>
          {lockTerms ? (
            <p className="text-red-700 dark:text-red-300">
              This loan has started repaying. Deleting it erases the record and its audit trail, and the
              remaining balance would no longer be collected. <strong>Suspend or archive instead</strong> to
              stop collection while keeping the history.
            </p>
          ) : (
            <p className="text-gray-700 dark:text-gray-200">Remove this deduction? This can’t be undone.</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {lockTerms && (
              <>
                <button onClick={() => quickStatus('suspended')} disabled={saving} className="px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">Suspend instead</button>
                <button onClick={() => quickStatus('archived')} disabled={saving} className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-50">Archive instead</button>
              </>
            )}
            <button onClick={remove} disabled={deleting} className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
              {deleting ? 'Deleting…' : lockTerms ? 'Delete anyway' : 'Delete'}
            </button>
            <button onClick={() => setConfirmDel(false)} className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">Keep</button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-3 mt-2 border-t border-gray-100 dark:border-gray-700">
        <div>
          {existing && !confirmDel && (
            <button
              onClick={() => setConfirmDel(true)}
              disabled={deleting}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl dark:hover:bg-red-900/30 disabled:opacity-50"
            >
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Remove
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl dark:text-gray-300 dark:hover:bg-gray-700">
            Cancel
          </button>
          <AccentButton icon={saving ? Loader2 : Save} disabled={saving} onClick={() => void save()}>
            {saving ? 'Saving…' : existing ? 'Save changes' : 'Add deduction'}
          </AccentButton>
        </div>
      </div>

      {/* NOT A BLOCK — A CONFIRMATION. Both of these are things the school
          legitimately does; what went wrong before was that they happened
          without anybody being told. */}
      <FormModal
        open={confirmWarnings}
        onClose={() => setConfirmWarnings(false)}
        icon={AlertTriangle}
        title="Before this is saved"
        subtitle="This deduction will not collect what you might expect."
        maxWidth="max-w-lg"
        footer={(
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setConfirmWarnings(false)}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Go back and change it
            </button>
            <AccentButton icon={Save} onClick={() => void save(true)}>
              Save anyway
            </AccentButton>
          </div>
        )}
      >
        <ul className="space-y-2">
          {pendingWarnings.map((w) => (
            <li key={w} className="flex gap-2 text-sm text-gray-700 dark:text-gray-200">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-500" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      </FormModal>

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => {
          if (saveModal.status === 'success') onClose();
          else setSaveModal((m) => ({ ...m, show: false }));
        }}
      />
    </Modal>
  );
}

/** Merge a select field's seed options with any bursar-added ones. */
function mergedOptions(f: FieldDef, settings?: HrPayrollSettings): string[] {
  if (f.kind !== 'select') return [];
  const extra = settings?.dropdowns?.[f.key] ?? [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const o of [...(f.options ?? []), ...extra]) {
    if (!seen.has(o)) { seen.add(o); out.push(o); }
  }
  return out;
}

function FieldInput({
  def,
  value,
  options,
  onChange,
  locked = false,
}: {
  def: FieldDef;
  value: string | boolean | undefined;
  options: string[];
  onChange: (v: string | boolean) => void;
  locked?: boolean;
}) {
  const readOnly = def.computed && (def.kind === 'money' || def.kind === 'number');
  /* A computed MONTH is set for you and cannot be changed: the salary
     advance's "Deducted from pay in" is the first payroll not yet done. */
  const fixedMonth = def.computed && def.kind === 'month';
  const lockCls = locked ? 'opacity-60 cursor-not-allowed' : '';
  // computed fields stay editable (per spec: "auto-calculated, editable"),
  // but we visually hint they're derived via the hint text.

  if (def.kind === 'checkbox') {
    return (
      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer py-1">
        <input type="checkbox" disabled={locked} checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} className="rounded" />
        {def.label}
      </label>
    );
  }

  const inner = (() => {
    switch (def.kind) {
      case 'textarea':
        return <textarea disabled={locked} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} rows={2} className={glassFieldClass(`py-2 text-sm ${lockCls}`)} />;
      case 'select':
        return (
          <select disabled={locked} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} className={glassFieldClass(`py-2 ${lockCls}`)}>
            <option value="">—</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      case 'date':
        return <input type="date" disabled={locked} value={String(value ?? '').slice(0, 10)} onChange={(e) => onChange(e.target.value)} className={glassFieldClass(`py-2 ${lockCls}`)} />;
      case 'month':
        return <input type="month" disabled={locked || fixedMonth} value={String(value ?? '').slice(0, 7)} onChange={(e) => onChange(e.target.value)} className={glassFieldClass(`py-2 ${lockCls} ${fixedMonth ? 'opacity-80 cursor-not-allowed' : ''}`)} />;
      case 'number':
      case 'money':
        return (
          <input
            type="number"
            inputMode="decimal"
            disabled={locked}
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            className={glassFieldClass(`py-2 ${readOnly ? 'opacity-80' : ''} ${lockCls}`)}
          />
        );
      default:
        return <input disabled={locked} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} className={glassFieldClass(`py-2 ${lockCls}`)} />;
    }
  })();

  return (
    <Field label={def.label + (def.required ? ' *' : '')} hint={def.hint}>
      {inner}
    </Field>
  );
}

function CustomFieldsEditor({
  fields,
  onChange,
}: {
  fields: { key: string; value: string }[];
  onChange: (f: { key: string; value: string }[]) => void;
}) {
  function set(i: number, patch: Partial<{ key: string; value: string }>) {
    onChange(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  }
  return (
    <div className="pt-2 border-t border-dashed border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Custom fields</span>
        <button onClick={() => onChange([...fields, { key: '', value: '' }])} className="flex items-center gap-1 text-xs font-medium text-primary dark:text-primarySoft hover:underline">
          <Plus size={12} /> Add field
        </button>
      </div>
      {fields.length === 0 ? (
        <p className="text-[11px] text-gray-400">Add any ad-hoc field this deduction needs.</p>
      ) : (
        <div className="space-y-2">
          {fields.map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={f.key} onChange={(e) => set(i, { key: e.target.value })} placeholder="Label" className={glassFieldClass('py-1.5 text-sm flex-1')} />
              <input value={f.value} onChange={(e) => set(i, { value: e.target.value })} placeholder="Value" className={glassFieldClass('py-1.5 text-sm flex-1')} />
              <button onClick={() => onChange(fields.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-red-500 p-1" title="Remove">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ==================================================================
   LOCAL UI — Modal + Field (kept local to avoid touching StaffProfile)
   ================================================================== */
function Modal({
  title,
  subtitle,
  icon: Icon,
  children,
  onClose,
}: {
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <FormModal onClose={onClose} icon={Icon} title={title} subtitle={subtitle} maxWidth="max-w-lg">
      {children}
    </FormModal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 block">{hint}</span>}
    </label>
  );
}
